import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, Users, User, Save, Check, Clock, AlertCircle, Wand2 } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';

// Demo data
const DEMO_STUDENTS = [
  { uniqueid: 1001, firstname: 'Emma', surname: 'Thompson', grade_level: '3', photo: null },
  { uniqueid: 1002, firstname: 'Liam', surname: 'O\'Brien', grade_level: '3', photo: null },
  { uniqueid: 1003, firstname: 'Sophia', surname: 'Chen', grade_level: '3', photo: null },
  { uniqueid: 1004, firstname: 'Noah', surname: 'Martinez', grade_level: '3', photo: null },
  { uniqueid: 1005, firstname: 'Olivia', surname: 'Patel', grade_level: '3', photo: null },
  { uniqueid: 1006, firstname: 'James', surname: 'Wilson', grade_level: '3', photo: null },
];

const DEMO_SKILLS = [
  { id: 1, skill_name: 'Reading Comprehension' },
  { id: 2, skill_name: 'Written Expression' },
  { id: 3, skill_name: 'Mathematical Reasoning' },
  { id: 4, skill_name: 'Problem Solving' },
];

const PROFICIENCY_LEVELS = [
  { code: 'EXT', name: 'Exceeding', color: '#10b981' },
  { code: 'SEC', name: 'Secure', color: '#3b82f6' },
  { code: 'DEV', name: 'Developing', color: '#f59e0b' },
  { code: 'EMG', name: 'Emerging', color: '#ef4444' },
  { code: '', name: 'Not Assessed', color: '#9ca3af' },
];

const GRADE_COLUMNS = [
  { id: 1, name: 'Performance S1' },
  { id: 2, name: 'Exam S1' },
  { id: 3, name: 'Performance S2' },
  { id: 4, name: 'Exam S2' },
];

const LETTER_GRADES = ['A', 'B', 'C', 'D', 'F', ''];

const UK_SPELLING = {
  "behavior": "behaviour", "behaviors": "behaviours",
  "color": "colour", "colors": "colours",
  "center": "centre", "centers": "centres",
  "organize": "organise", "organized": "organised",
  "realize": "realise", "realized": "realised",
  "analyze": "analyse", "analyzed": "analysed",
  "recognize": "recognise", "recognized": "recognised",
  "favorite": "favourite", "favorites": "favourites",
};

const IshworldTeacherGradebookDemo = () => {
  const [gradebookType, setGradebookType] = useState('primary');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [assessments, setAssessments] = useState({});
  const [narratives, setNarratives] = useState({});
  const [grades, setGrades] = useState({});
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [magicFixApplied, setMagicFixApplied] = useState(false);
  const saveTimerRef = useRef(null);

  // Initialize demo data
  useEffect(() => {
    const initialAssessments = {};
    const initialNarratives = {};
    const initialGrades = {};
    
    DEMO_STUDENTS.forEach(student => {
      initialAssessments[student.uniqueid] = {
        1: 'SEC', 2: 'DEV', 3: 'SEC', 4: 'EXT'
      };
      
      if (gradebookType === 'primary') {
        initialNarratives[student.uniqueid] = {
          strength: `${student.firstname} demonstrates strong reading skills and actively participates in class discussions.`,
          steps: `Continue to practice writing complete sentences with proper punctuation.`,
          status: 'draft'
        };
      } else {
        initialNarratives[student.uniqueid] = {
          text: `${student.firstname} shows consistent effort in mathematics and has improved problem-solving abilities throughout the semester.`,
          status: 'draft'
        };
        initialGrades[student.uniqueid] = {
          1: 'A', 2: 'B', 3: 'A', 4: 'B'
        };
      }
    });
    
    setAssessments(initialAssessments);
    setNarratives(initialNarratives);
    setGrades(initialGrades);
  }, [gradebookType]);

  // Auto-save simulation
  const scheduleAutoSave = (studentId) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    
    saveTimerRef.current = setTimeout(() => {
      setSaving(true);
      setTimeout(() => {
        setSaving(false);
        setLastSaved(new Date());
      }, 500);
    }, 2000);
  };

  // Magic Fix function
  const applyMagicFix = (text, studentName, isPrimary = false) => {
    let fixed = text;
    
    // UK spelling conversion
    Object.entries(UK_SPELLING).forEach(([us, uk]) => {
      const regex = new RegExp(`\\b${us}\\b`, 'gi');
      fixed = fixed.replace(regex, uk);
    });
    
    // Capitalize sentences
    fixed = fixed.replace(/(^\w|[.!?]\s+\w)/g, match => match.toUpperCase());
    
    // Ensure ends with period
    if (fixed.trim() && !fixed.trim().match(/[.!?]$/)) {
      fixed = fixed.trim() + '.';
    }
    
    // Clean multiple spaces
    fixed = fixed.replace(/\s+/g, ' ');
    
    // First mention uses student name (simplified)
    if (isPrimary) {
      fixed = fixed.replace(/^(He|She|They)\s/, `${studentName} `);
    }
    
    return fixed;
  };

  const handleNarrativeChange = (studentId, field, value) => {
    setNarratives(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value
      }
    }));
    scheduleAutoSave(studentId);
  };

  const handleNarrativeBlur = (studentId, field, value) => {
    const student = DEMO_STUDENTS.find(s => s.uniqueid === studentId);
    const fixed = applyMagicFix(value, student.firstname, gradebookType === 'primary');
    
    if (fixed !== value) {
      setNarratives(prev => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          [field]: fixed
        }
      }));
      setMagicFixApplied(true);
      setTimeout(() => setMagicFixApplied(false), 2000);
    }
  };

  const handleAssessmentChange = (studentId, skillId, proficiency) => {
    setAssessments(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [skillId]: proficiency
      }
    }));
  };

  const handleGradeChange = (studentId, gradeColId, grade) => {
    setGrades(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [gradeColId]: grade
      }
    }));
  };

  const getCompletionStatus = (studentId) => {
    const studentNarrative = narratives[studentId];
    const studentAssessments = assessments[studentId];
    const studentGrades = grades[studentId];
    
    if (gradebookType === 'primary') {
      const hasStrength = studentNarrative?.strength?.length > 50;
      const hasSteps = studentNarrative?.steps?.length > 50;
      const allSkillsFilled = DEMO_SKILLS.every(skill => studentAssessments?.[skill.id]);
      
      if (hasStrength && hasSteps && allSkillsFilled) return 'complete';
      if (hasStrength || hasSteps || Object.keys(studentAssessments || {}).length > 0) return 'in_progress';
      return 'not_started';
    } else {
      const hasNarrative = studentNarrative?.text?.length > 50;
      const allSkillsFilled = DEMO_SKILLS.every(skill => studentAssessments?.[skill.id]);
      const allGradesFilled = GRADE_COLUMNS.every(col => studentGrades?.[col.id]);
      
      if (hasNarrative && allSkillsFilled && allGradesFilled) return 'complete';
      if (hasNarrative || Object.keys(studentAssessments || {}).length > 0 || Object.keys(studentGrades || {}).length > 0) return 'in_progress';
      return 'not_started';
    }
  };

  const getCompletionStats = () => {
    const stats = { complete: 0, in_progress: 0, not_started: 0 };
    DEMO_STUDENTS.forEach(student => {
      const status = getCompletionStatus(student.uniqueid);
      stats[status]++;
    });
    return stats;
  };

  const stats = getCompletionStats();
  const completionPercentage = Math.round((stats.complete / DEMO_STUDENTS.length) * 100);

  const getAvatar = (student) => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
    const color = colors[student.uniqueid % colors.length];
    const initials = `${student.firstname[0]}${student.surname[0]}`;
    
    return (
      <div className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
           style={{ width: 48, height: 48, backgroundColor: color, fontSize: 18 }}>
        {initials}
      </div>
    );
  };

  const renderGridView = () => (
    <div className="table-responsive">
      <table className="table table-bordered table-hover">
        <thead className="table-light">
          <tr>
            <th style={{ width: 200 }}>Student</th>
            {DEMO_SKILLS.map(skill => (
              <th key={skill.id} style={{ width: 150 }}>{skill.skill_name}</th>
            ))}
            {gradebookType === 'secondary' && GRADE_COLUMNS.map(col => (
              <th key={col.id} style={{ width: 120 }}>{col.name}</th>
            ))}
            <th style={{ width: 100 }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {DEMO_STUDENTS.map(student => {
            const status = getCompletionStatus(student.uniqueid);
            const statusColors = {
              complete: 'success',
              in_progress: 'warning',
              not_started: 'secondary'
            };
            
            return (
              <tr key={student.uniqueid} style={{ cursor: 'pointer' }}
                  onClick={() => { setSelectedStudent(student); setViewMode('individual'); }}>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    {getAvatar(student)}
                    <div>
                      <div className="fw-semibold">{student.firstname} {student.surname}</div>
                      <small className="text-muted">Grade {student.grade_level}</small>
                    </div>
                  </div>
                </td>
                {DEMO_SKILLS.map(skill => {
                  const proficiency = assessments[student.uniqueid]?.[skill.id];
                  const level = PROFICIENCY_LEVELS.find(p => p.code === proficiency);
                  return (
                    <td key={skill.id} onClick={e => e.stopPropagation()}>
                      <select 
                        className="form-select form-select-sm"
                        value={proficiency || ''}
                        onChange={e => handleAssessmentChange(student.uniqueid, skill.id, e.target.value)}
                        style={{ 
                          backgroundColor: level?.color || '#f3f4f6',
                          color: proficiency ? 'white' : '#6b7280',
                          fontWeight: 500
                        }}>
                        {PROFICIENCY_LEVELS.map(p => (
                          <option key={p.code} value={p.code}>{p.name}</option>
                        ))}
                      </select>
                    </td>
                  );
                })}
                {gradebookType === 'secondary' && GRADE_COLUMNS.map(col => (
                  <td key={col.id} onClick={e => e.stopPropagation()}>
                    <select 
                      className="form-select form-select-sm"
                      value={grades[student.uniqueid]?.[col.id] || ''}
                      onChange={e => handleGradeChange(student.uniqueid, col.id, e.target.value)}>
                      {LETTER_GRADES.map(g => (
                        <option key={g} value={g}>{g || 'Not Graded'}</option>
                      ))}
                    </select>
                  </td>
                ))}
                <td>
                  <span className={`badge bg-${statusColors[status]}`}>
                    {status.replace('_', ' ').toUpperCase()}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderListView = () => (
    <div className="row g-3">
      {DEMO_STUDENTS.map(student => {
        const status = getCompletionStatus(student.uniqueid);
        const statusColors = {
          complete: 'success',
          in_progress: 'warning',
          not_started: 'secondary'
        };
        
        return (
          <div key={student.uniqueid} className="col-md-6">
            <div className="card h-100 shadow-sm" style={{ cursor: 'pointer' }}
                 onClick={() => { setSelectedStudent(student); setViewMode('individual'); }}>
              <div className="card-body">
                <div className="d-flex align-items-center gap-3 mb-3">
                  {getAvatar(student)}
                  <div className="flex-grow-1">
                    <h5 className="mb-0">{student.firstname} {student.surname}</h5>
                    <small className="text-muted">Grade {student.grade_level}</small>
                  </div>
                  <span className={`badge bg-${statusColors[status]}`}>
                    {status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                
                <div className="mb-2">
                  <strong className="d-block mb-2">Skills Assessment:</strong>
                  <div className="row g-2">
                    {DEMO_SKILLS.map(skill => {
                      const proficiency = assessments[student.uniqueid]?.[skill.id];
                      const level = PROFICIENCY_LEVELS.find(p => p.code === proficiency);
                      return (
                        <div key={skill.id} className="col-6">
                          <small className="text-muted d-block">{skill.skill_name}</small>
                          <span className="badge" style={{ backgroundColor: level?.color || '#9ca3af' }}>
                            {level?.name || 'Not Assessed'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {gradebookType === 'secondary' && (
                  <div className="mt-2">
                    <strong className="d-block mb-2">Grades:</strong>
                    <div className="d-flex gap-2 flex-wrap">
                      {GRADE_COLUMNS.map(col => (
                        <span key={col.id} className="badge bg-primary">
                          {col.name}: {grades[student.uniqueid]?.[col.id] || '-'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderIndividualView = () => {
    if (!selectedStudent) {
      return (
        <div className="alert alert-info">
          <AlertCircle className="me-2" size={20} />
          Select a student to view their individual assessment
        </div>
      );
    }

    const studentNarrative = narratives[selectedStudent.uniqueid] || {};
    const charLimit = 1500;

    return (
      <div className="card shadow">
        <div className="card-header bg-primary text-white">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              {getAvatar(selectedStudent)}
              <div>
                <h4 className="mb-0">{selectedStudent.firstname} {selectedStudent.surname}</h4>
                <small>Grade {selectedStudent.grade_level}</small>
              </div>
            </div>
            <button className="btn btn-light btn-sm" onClick={() => setSelectedStudent(null)}>
              Back to {viewMode === 'list' ? 'List' : 'Grid'}
            </button>
          </div>
        </div>
        
        <div className="card-body">
          {/* Skills Assessment */}
          <div className="mb-4">
            <h5 className="mb-3">Skills Assessment</h5>
            <div className="row g-3">
              {DEMO_SKILLS.map(skill => {
                const proficiency = assessments[selectedStudent.uniqueid]?.[skill.id];
                const level = PROFICIENCY_LEVELS.find(p => p.code === proficiency);
                return (
                  <div key={skill.id} className="col-md-6">
                    <label className="form-label fw-semibold">{skill.skill_name}</label>
                    <select 
                      className="form-select"
                      value={proficiency || ''}
                      onChange={e => handleAssessmentChange(selectedStudent.uniqueid, skill.id, e.target.value)}
                      style={{ 
                        backgroundColor: level?.color || '#f3f4f6',
                        color: proficiency ? 'white' : '#6b7280',
                        fontWeight: 500
                      }}>
                      {PROFICIENCY_LEVELS.map(p => (
                        <option key={p.code} value={p.code}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Grades (Secondary only) */}
          {gradebookType === 'secondary' && (
            <div className="mb-4">
              <h5 className="mb-3">Grades</h5>
              <div className="row g-3">
                {GRADE_COLUMNS.map(col => (
                  <div key={col.id} className="col-md-6">
                    <label className="form-label fw-semibold">{col.name}</label>
                    <select 
                      className="form-select"
                      value={grades[selectedStudent.uniqueid]?.[col.id] || ''}
                      onChange={e => handleGradeChange(selectedStudent.uniqueid, col.id, e.target.value)}>
                      {LETTER_GRADES.map(g => (
                        <option key={g} value={g}>{g || 'Not Graded'}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Narratives */}
          <div>
            <h5 className="mb-3">
              {gradebookType === 'primary' ? 'Teacher Comments' : 'Student Narrative'}
            </h5>
            
            {gradebookType === 'primary' ? (
              <>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Areas of Strength</label>
                  <textarea 
                    className="form-control"
                    rows="4"
                    maxLength={charLimit}
                    value={studentNarrative.strength || ''}
                    onChange={e => handleNarrativeChange(selectedStudent.uniqueid, 'strength', e.target.value)}
                    onBlur={e => handleNarrativeBlur(selectedStudent.uniqueid, 'strength', e.target.value)}
                    placeholder={`Describe ${selectedStudent.firstname}'s strengths...`}
                  />
                  <small className={`text-${(studentNarrative.strength?.length || 0) > charLimit - 100 ? 'danger' : 'muted'}`}>
                    {studentNarrative.strength?.length || 0} / {charLimit}
                  </small>
                </div>
                
                <div className="mb-3">
                  <label className="form-label fw-semibold">Next Steps</label>
                  <textarea 
                    className="form-control"
                    rows="4"
                    maxLength={charLimit}
                    value={studentNarrative.steps || ''}
                    onChange={e => handleNarrativeChange(selectedStudent.uniqueid, 'steps', e.target.value)}
                    onBlur={e => handleNarrativeBlur(selectedStudent.uniqueid, 'steps', e.target.value)}
                    placeholder={`Describe next steps for ${selectedStudent.firstname}...`}
                  />
                  <small className={`text-${(studentNarrative.steps?.length || 0) > charLimit - 100 ? 'danger' : 'muted'}`}>
                    {studentNarrative.steps?.length || 0} / {charLimit}
                  </small>
                </div>
              </>
            ) : (
              <div className="mb-3">
                <textarea 
                  className="form-control"
                  rows="6"
                  maxLength={charLimit}
                  value={studentNarrative.text || ''}
                  onChange={e => handleNarrativeChange(selectedStudent.uniqueid, 'text', e.target.value)}
                  onBlur={e => handleNarrativeBlur(selectedStudent.uniqueid, 'text', e.target.value)}
                  placeholder={`Write narrative for ${selectedStudent.firstname}...`}
                />
                <small className={`text-${(studentNarrative.text?.length || 0) > charLimit - 100 ? 'danger' : 'muted'}`}>
                  {studentNarrative.text?.length || 0} / {charLimit}
                </small>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid py-4" style={{ maxWidth: 1400 }}>
      {/* Header */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h2 className="mb-2">
                <BookOpen className="me-2" size={28} />
                ISHWorld Teacher Gradebook Demo
              </h2>
              <p className="text-muted mb-0">Interactive demonstration of gradebook features</p>
            </div>
            
            <div className="col-md-6">
              <div className="d-flex gap-2 justify-content-md-end flex-wrap">
                <button 
                  className={`btn ${gradebookType === 'primary' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setGradebookType('primary')}>
                  Primary (K-5)
                </button>
                <button 
                  className={`btn ${gradebookType === 'secondary' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setGradebookType('secondary')}>
                  Secondary (6-12)
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card shadow-sm border-success">
            <div className="card-body text-center">
              <Check className="text-success mb-2" size={32} />
              <h3 className="mb-0">{stats.complete}</h3>
              <small className="text-muted">Complete</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm border-warning">
            <div className="card-body text-center">
              <Clock className="text-warning mb-2" size={32} />
              <h3 className="mb-0">{stats.in_progress}</h3>
              <small className="text-muted">In Progress</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm border-secondary">
            <div className="card-body text-center">
              <AlertCircle className="text-secondary mb-2" size={32} />
              <h3 className="mb-0">{stats.not_started}</h3>
              <small className="text-muted">Not Started</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm border-primary">
            <div className="card-body text-center">
              <div className="mb-2">
                <div className="progress" style={{ height: 30 }}>
                  <div className="progress-bar bg-primary" style={{ width: `${completionPercentage}%` }}>
                    <strong>{completionPercentage}%</strong>
                  </div>
                </div>
              </div>
              <small className="text-muted">Overall Completion</small>
            </div>
          </div>
        </div>
      </div>

      {/* View Mode Controls */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div className="btn-group">
              <button 
                className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => { setViewMode('grid'); setSelectedStudent(null); }}>
                <Users size={18} className="me-1" /> Grid View
              </button>
              <button 
                className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => { setViewMode('list'); setSelectedStudent(null); }}>
                <BookOpen size={18} className="me-1" /> List View
              </button>
              <button 
                className={`btn ${viewMode === 'individual' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setViewMode('individual')}>
                <User size={18} className="me-1" /> Individual
              </button>
            </div>
            
            <div className="d-flex align-items-center gap-3">
              {magicFixApplied && (
                <div className="alert alert-success mb-0 py-1 px-2 d-flex align-items-center">
                  <Wand2 size={16} className="me-2" />
                  <small>Magic Fix Applied!</small>
                </div>
              )}
              {saving && (
                <div className="text-muted d-flex align-items-center">
                  <Save size={16} className="me-2" />
                  <small>Saving...</small>
                </div>
              )}
              {lastSaved && !saving && (
                <div className="text-success d-flex align-items-center">
                  <Check size={16} className="me-2" />
                  <small>Saved {lastSaved.toLocaleTimeString()}</small>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <strong className="d-block mb-2">Proficiency Legend:</strong>
          <div className="d-flex gap-3 flex-wrap">
            {PROFICIENCY_LEVELS.filter(p => p.code).map(level => (
              <span key={level.code} className="badge d-flex align-items-center gap-2"
                    style={{ backgroundColor: level.color, fontSize: 14, padding: '8px 12px' }}>
                <strong>{level.code}</strong> = {level.name}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="card shadow">
        <div className="card-body">
          {viewMode === 'grid' && renderGridView()}
          {viewMode === 'list' && renderListView()}
          {viewMode === 'individual' && renderIndividualView()}
        </div>
      </div>

      {/* Feature Highlights */}
      <div className="card shadow-sm mt-4">
        <div className="card-header bg-light">
          <h5 className="mb-0">✨ Interactive Features Demo</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <h6><Wand2 size={18} className="me-2 text-primary" />Magic Fix (Auto-Correction)</h6>
              <ul className="small mb-0">
                <li>Type "color" → converts to "colour" (UK spelling)</li>
                <li>Automatically capitalizes sentences</li>
                <li>Adds final period if missing</li>
                <li>Cleans up multiple spaces</li>
                <li>Triggers 2 seconds after typing stops</li>
              </ul>
            </div>
            <div className="col-md-6">
              <h6><Save size={18} className="me-2 text-success" />Auto-Save</h6>
              <ul className="small mb-0">
                <li>Saves automatically after 2 seconds of inactivity</li>
                <li>Silent operation (no notifications)</li>
                <li>Shows "Saving..." indicator</li>
                <li>Displays last saved time</li>
              </ul>
            </div>
            <div className="col-md-6">
              <h6><Users size={18} className="me-2 text-warning" />Three View Modes</h6>
              <ul className="small mb-0">
                <li><strong>Grid:</strong> Full class overview in table</li>
                <li><strong>List:</strong> Student cards with summaries</li>
                <li><strong>Individual:</strong> Single student detail view</li>
              </ul>
            </div>
            <div className="col-md-6">
              <h6><Check size={18} className="me-2 text-info" />Completion Tracking</h6>
              <ul className="small mb-0">
                <li>Real-time status updates</li>
                <li>Color-coded badges</li>
                <li>Overall completion percentage</li>
                <li>Primary: Checks both narratives + all skills</li>
                <li>Secondary: Checks narrative + skills + grades</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Try It Section */}
      <div className="alert alert-info mt-4">
        <h5><AlertCircle size={20} className="me-2" />Try These Features:</h5>
        <ol className="mb-0">
          <li>Click on a student in Grid or List view to open Individual view</li>
          <li>Change skill proficiency levels and see color coding</li>
          <li>Edit a narrative text and type "behavior" - it will change to "behaviour" when you click away</li>
          <li>Watch the character counter turn red as you approach the limit (1500 chars)</li>
          <li>Notice the auto-save indicator after you stop typing for 2 seconds</li>
          <li>Switch between Primary and Secondary gradebook types to see the differences</li>
        </ol>
      </div>
    </div>
  );
};

export default IshworldTeacherGradebookDemo;